import { useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import moment from "moment";
import "./PdfPrecario.scss";

function PdfPrecario({
  tratamentos,
  clinica,
  data,
  instituicaoId,
  instituicoes,
  onClose,
}) {
  const componentRef = useRef();
  const dataFormatada = moment(data).format("DD/MM/YYYY");
  const dataBusca = new Date(data);

  // Determinar o tipo de preçário e nome da instituição
  const isParticular = instituicaoId === "particular";
  const instituicaoNome = isParticular
    ? "PARTICULAR"
    : instituicoes.find((i) => i.id === instituicaoId)?.nome || "";

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Precario-${instituicaoNome}-${moment(data).format(
      "DD-MM-YYYY",
    )}`,
    onAfterPrint: () => {
      onClose();
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    handlePrint();
  }, [handlePrint]);

  // Função para buscar preço na data especificada
  const getPrecoNaData = (tratamento) => {
    // Se não há histórico, usa o preço atual
    if (
      !tratamento.historicoPrecos ||
      tratamento.historicoPrecos.length === 0
    ) {
      if (isParticular) {
        return tratamento.preco_particular;
      } else {
        const precosInst = tratamento.precosPorInstituicao?.find(
          (p) =>
            p.instituicao.id === instituicaoId ||
            p.instituicao._id === instituicaoId,
        );
        return {
          preco_segurado: precosInst?.preco_segurado || 0,
          preco_beneficiario: precosInst?.preco_beneficiario || 0,
          comparticipacao: precosInst?.comparticipacao || 0,
          coberto: precosInst?.coberto || false,
        };
      }
    }

    // Ordenar histórico por data (mais recente primeiro)
    const historicoOrdenado = [...tratamento.historicoPrecos].sort(
      (a, b) => new Date(b.data) - new Date(a.data),
    );

    // Buscar o primeiro histórico com data menor ou igual à data buscada
    const historicoAplicavel = historicoOrdenado.find(
      (h) => new Date(h.data) <= dataBusca,
    );

    if (historicoAplicavel) {
      if (isParticular) {
        return historicoAplicavel.preco_particular;
      } else {
        const precosInst = historicoAplicavel.precosPorInstituicao?.find(
          (p) =>
            p.instituicao === instituicaoId ||
            p.instituicao.toString() === instituicaoId,
        );
        return {
          preco_segurado: precosInst?.preco_segurado || 0,
          preco_beneficiario: precosInst?.preco_beneficiario || 0,
          comparticipacao: precosInst?.comparticipacao || 0,
          coberto: precosInst?.coberto || false,
        };
      }
    }

    // Se não encontrou no histórico, usa o preço atual
    if (isParticular) {
      return tratamento.preco_particular;
    } else {
      const precosInst = tratamento.precosPorInstituicao?.find(
        (p) =>
          p.instituicao.id === instituicaoId ||
          p.instituicao._id === instituicaoId,
      );
      return {
        preco_segurado: precosInst?.preco_segurado || 0,
        preco_beneficiario: precosInst?.preco_beneficiario || 0,
        comparticipacao: precosInst?.comparticipacao || 0,
        coberto: precosInst?.coberto || false,
      };
    }
  };

  return (
    <div
      ref={componentRef}
      className={`pdf-precario-wrapper ${clinica.configuracoes?.configuracoes?.impressaoPretoEBranco?.ativo ? " pdf-wrapper--pretoebranco" : ""}`}
    >
      <div className="pdf-precario-cabecalho">
        <div className="pdf-precario-cabecalho-left">
          <img
            src={`${process.env.REACT_APP_BACKEND_LINK}/${clinica.logotipo}`}
            alt="Logo"
          />
        </div>

        <div className="pdf-precario-cabecalho-right">
          <span className="clinica-nome">{clinica.nome}</span>
          {/* Os três contactos, como nos restantes impressos. */}
          <span>
            Telef: {clinica.contacto}
            {clinica.contacto2 ? `/${clinica.contacto2}` : ""}
            {clinica.contacto3 ? `/${clinica.contacto3}` : ""}
          </span>
          <span>{clinica.endereco}</span>
          <span>NIF: {clinica.nif}</span>
        </div>
      </div>

      <div className="pdf-precario-titulo">
        <h1>PREÇÁRIO - {instituicaoNome}</h1>
        <h2>Válido em: {dataFormatada}</h2>
      </div>

      {/* SUBSTITUIR todo o map de categorias por uma única tabela */}
      <table className="tabela-precario">
        <thead>
          <tr>
            {!isParticular && <th className="col-codigo">Código</th>}
            <th className="col-designacao">Designação</th>
            {isParticular ? (
              <th className="col-preco">Preço Particular</th>
            ) : (
              <>
                <th className="col-preco">Preço Segurado</th>
                <th className="col-preco">Comparticipação</th>
                <th className="col-preco">Beneficiário</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {tratamentos.map((tratamento) => {
            const preco = getPrecoNaData(tratamento);

            // Se for instituição e não estiver coberto, pular
            if (!isParticular && typeof preco === "object" && !preco.coberto) {
              return null;
            }

            return (
              <tr key={tratamento.id}>
                {!isParticular && <td>{tratamento.codigo}</td>}
                <td>{tratamento.designacao}</td>
                {isParticular ? (
                  <td className="preco-valor">
                    {Intl.NumberFormat("pt-BR").format(preco)}$00
                  </td>
                ) : (
                  <>
                    <td className="preco-valor">
                      {Intl.NumberFormat("pt-BR").format(preco.preco_segurado)}
                      $00
                    </td>
                    <td className="preco-valor">
                      {Intl.NumberFormat("pt-BR").format(preco.comparticipacao)}
                      $00
                    </td>
                    <td className="preco-valor">
                      {Intl.NumberFormat("pt-BR").format(
                        preco.preco_beneficiario,
                      )}
                      $00
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pdf-precario-rodape">
        <p>Preçário gerado em {moment().format("DD/MM/YYYY")}</p>
        <p>
          OdontoHT — Gestão inteligente para clínicas dentárias · by HealthTech
          CV
        </p>
      </div>
    </div>
  );
}

export default PdfPrecario;
